<template>
  <div class="action-center-wrap px-2 pt-2 pb-4">
    <SchemaSolutionPanel
      v-if="showingSolutionDetail"
      :diagnosis-report-id="selectedFindingReportId"
    />

    <DiagnosisFindingDetail
      v-else-if="showingFindingDetail"
      :finding-id="selectedFindingId"
      :diagnosis-findings="diagnosisFindings"
      :diagnosis-preview="diagnosisPreview"
      :queries="queries"
      :review-entity="reviewEntity"
    />

    <div v-show="!showingFindingDetail">
    <div class="d-flex align-center mb-4">
      <v-icon class="mr-3 action-center-header-lightning" aria-hidden="true">mdi-lightning-bolt-outline</v-icon>
      <span class="text-h5 font-weight-bold">Action Center</span>
    </div>

    <div class="action-center-content pa-6 pt-0">
      <div class="text-body-2 text-medium-emphasis mb-4">
        Diagnosis reports prioritized by severity and effort.
      </div>

      <v-alert
        v-if="showDiagnosisProgress"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        <div class="d-flex align-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="18"
            width="2"
            class="mr-3"
          />
          <span>
            {{ diagnosisProgressMessage }}
          </span>
        </div>
      </v-alert>

      <DiagnosisRunningCard
        v-if="showDiagnosisRunningCard"
        :provider-count="diagnosisProviderCount"
        :match-string-count="matchStringCount"
        :dismissals="diagnosisDismissals"
      />

      <DiagnosisSetupChecklist
        v-if="showSetupChecklist"
        :provider-count="diagnosisProviderCount"
        :match-string-count="matchStringCount"
        :dismissals="diagnosisDismissals"
        :running="startingDiagnosis"
        :busy="savingDismissal || startingDiagnosis"
        :error="diagnosisSetupError"
        :providers-pending="providerComparisonPending"
        @go-to-tab="$emit('go-to-tab', $event)"
        @set-dismissal="setDiagnosisDismissal"
        @run="runDiagnosis"
      />

      <DiagnosisUpgradeCard v-if="showDiagnosisUpgradeCard" />

      <v-alert
        v-if="showDiagnosisUnavailableAlert"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        {{ diagnosisUnavailableMessage }}
      </v-alert>

      <template v-if="allFindings.length > 0">
        <v-card variant="outlined" class="action-center-card mb-4">
          <v-card-text class="pa-4">
            <v-row class="summary-row">
              <v-col cols="12" sm="6" md="3">
                <button
                  type="button"
                  class="summary-metric-card summary-metric-critical"
                  :class="{ 'summary-metric-active': selectedFilter === 'critical' }"
                  @click="toggleFilter('critical')"
                >
                  <div v-if="selectedFilter === 'critical'" class="summary-metric-selected-indicator">Selected</div>
                  <div class="summary-label">Critical findings</div>
                  <div class="summary-value">{{ criticalCount }}</div>
                </button>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <button
                  type="button"
                  class="summary-metric-card summary-metric-high"
                  :class="{ 'summary-metric-active': selectedFilter === 'high' }"
                  @click="toggleFilter('high')"
                >
                  <div v-if="selectedFilter === 'high'" class="summary-metric-selected-indicator">Selected</div>
                  <div class="summary-label">High priority</div>
                  <div class="summary-value">{{ highPriorityCount }}</div>
                </button>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <button
                  type="button"
                  class="summary-metric-card summary-metric-medium"
                  :class="{ 'summary-metric-active': selectedFilter === 'medium' }"
                  @click="toggleFilter('medium')"
                >
                  <div v-if="selectedFilter === 'medium'" class="summary-metric-selected-indicator">Selected</div>
                  <div class="summary-label">Medium priority</div>
                  <div class="summary-value">{{ mediumPriorityCount }}</div>
                </button>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <button
                  type="button"
                  class="summary-metric-card summary-metric-quick"
                  :class="{ 'summary-metric-active': selectedFilter === 'quick_wins' }"
                  @click="toggleFilter('quick_wins')"
                >
                  <div v-if="selectedFilter === 'quick_wins'" class="summary-metric-selected-indicator">Selected</div>
                  <div class="summary-label">Quick wins</div>
                  <div class="summary-value">{{ lowEffortCount }}</div>
                </button>
              </v-col>
            </v-row>
            <div v-if="selectedFilter !== 'all'" class="text-caption text-medium-emphasis mt-2">
              Filter applied: {{ filterLabel }}.
              <button type="button" class="clear-filter-btn" @click="selectedFilter = 'all'">Clear</button>
            </div>
          </v-card-text>
        </v-card>

        <div class="action-sections">
          <template v-if="isGroupedView">
            <div
              v-for="group in groupedFindings"
              :key="`group-${group.severity}`"
              class="action-section mb-6"
            >
              <button
                v-if="!group.count"
                type="button"
                class="section-heading section-toggle section-heading-empty"
                @click="toggleSection(group.severity)"
              >
                {{ group.label.toUpperCase() }} (0)
                <v-icon size="14">{{ isSectionExpanded(group.severity) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </button>
              <template v-if="group.count || isSectionExpanded(group.severity)">
                <div
                  v-for="bucket in group.buckets"
                  :key="`${group.severity}-${bucket.key}`"
                  class="action-bucket"
                >
                  <div v-if="bucket.count" class="section-heading">{{ bucketHeading(group, bucket) }}</div>
                  <template v-else>
                    <button
                      type="button"
                      class="section-heading section-toggle section-heading-empty"
                      @click="toggleSection(`${group.severity}-${bucket.key}`)"
                    >
                      {{ bucketHeading(group, bucket) }}
                      <v-icon size="14">
                        {{ isSectionExpanded(`${group.severity}-${bucket.key}`) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                      </v-icon>
                    </button>
                    <div
                      v-if="isSectionExpanded(`${group.severity}-${bucket.key}`)"
                      class="text-body-2 text-medium-emphasis mb-3"
                    >
                      No items in this bucket.
                    </div>
                  </template>
                  <template v-for="(item, idx) in bucket.items" :key="`${group.severity}-${bucket.key}-${item.key}`">
                    <ActionCenterUnlockBanner
                      v-if="lacksIntelligencePlan && item.key === firstBlurredItemKey"
                      :preview-shown="unlockBannerPreviewShown"
                      :total-actions="unlockBannerTotalActions"
                      :critical-count="criticalCount"
                      :high-count="highPriorityCount"
                      :quick-wins-count="lowEffortCount"
                    />
                    <ActionCenterFindingCard
                      :item="item"
                      :idx="idx"
                      :blurred="shouldBlurItem(item)"
                      :highlighted="isHighlightedFinding(item)"
                      :total-query-count="totalQueryCount"
                      :affected-queries="affectedQueries(item)"
                      :affected-expanded="isAffectedExpanded(item)"
                      @toggle-affected="toggleAffectedQueries(item)"
                      @view-details="openFinding(item)"
                    />
                  </template>
                </div>
              </template>
            </div>
          </template>

          <template v-else>
            <div class="action-section">
              <div class="section-heading">{{ flatListHeading }}</div>
              <template v-for="(item, idx) in filteredFindingsVisible" :key="`filtered-${item.key}`">
                <ActionCenterUnlockBanner
                  v-if="lacksIntelligencePlan && item.key === firstBlurredItemKey"
                  :preview-shown="unlockBannerPreviewShown"
                  :total-actions="unlockBannerTotalActions"
                  :critical-count="criticalCount"
                  :high-count="highPriorityCount"
                  :quick-wins-count="lowEffortCount"
                />
                <ActionCenterFindingCard
                  :item="item"
                  :idx="idx"
                  :blurred="shouldBlurItem(item)"
                  :highlighted="isHighlightedFinding(item)"
                  :total-query-count="totalQueryCount"
                  :affected-queries="affectedQueries(item)"
                  :affected-expanded="isAffectedExpanded(item)"
                  @toggle-affected="toggleAffectedQueries(item)"
                  @view-details="openFinding(item)"
                />
              </template>
              <div v-if="!filteredFindingsVisible.length" class="text-body-2 text-medium-emphasis">No items for this filter.</div>
            </div>
          </template>

          <div v-if="showPaywallMoreActionsFooter" class="action-center-paywall-footer">
            <p class="action-center-paywall-footer-hint">
              + {{ remainingHiddenActionsCount }} more
              {{ remainingHiddenActionsCount === 1 ? 'action' : 'actions' }} across high priority, medium, and quick wins
            </p>
            <v-btn
              variant="flat"
              class="brand-upgrade-cta-btn text-none font-weight-bold"
              rounded="pill"
              size="large"
              @click="$router.push('/profile?tab=billing')"
            >
              Unlock full action plan →
            </v-btn>
          </div>
        </div>
      </template>
    </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios.js'
import { mapGetters } from 'vuex'
import ActionCenterUnlockBanner from './ActionCenterUnlockBanner.vue'
import DiagnosisRunningCard from './DiagnosisRunningCard.vue'
import DiagnosisSetupChecklist from './DiagnosisSetupChecklist.vue'
import DiagnosisUpgradeCard from './DiagnosisUpgradeCard.vue'
import ActionCenterFindingCard from './ActionCenterFindingCard.vue'
import DiagnosisFindingDetail from './DiagnosisFindingDetail.vue'
import SchemaSolutionPanel from './SchemaSolutionPanel.vue'
import {
  affectedQueriesFor,
  findingIsWithinPaywallDisplay,
  normalizeFindings,
  paywallPreview,
  paywallPreviewOrder,
  shouldBlurFinding,
  sortFindingsByPriority,
  withFindingKeys
} from '@/utils/diagnosisFindings'

const BASE_URL = process.env.VUE_APP_API_BASE_URL

const SEVERITY_FILTERS = ['critical', 'high', 'medium']
const SEVERITY_GROUPS = [...SEVERITY_FILTERS, 'low']
const SEVERITY_LABELS = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' }
const EFFORT_BUCKETS = [
  { key: 'do_first', label: 'Do First', effortLabel: 'Low effort' },
  { key: 'plan_next', label: 'Plan Next', effortLabel: 'Medium effort' },
  { key: 'evaluate', label: 'Evaluate', effortLabel: 'High effort' }
]

export default {
  name: 'ActionCenterTab',
  components: {
    ActionCenterUnlockBanner,
    ActionCenterFindingCard,
    DiagnosisFindingDetail,
    DiagnosisRunningCard,
    DiagnosisSetupChecklist,
    DiagnosisUpgradeCard,
    SchemaSolutionPanel
  },
  props: {
    assessmentId: {
      type: Number,
      default: null
    },
    reviewEntityId: {
      type: [Number, String],
      default: null
    },
    reviewEntity: {
      type: Object,
      default: null
    },
    selectedProvider: {
      type: String,
      default: null
    },
    queries: {
      type: Array,
      default: () => []
    },
    diagnosisFindings: {
      type: Array,
      default: () => []
    },
    diagnosisPreview: {
      type: Object,
      default: null
    },
    diagnosisSetup: {
      type: Object,
      default: null
    },
    providerComparisonPending: {
      type: Boolean,
      default: false
    }
  },
  emits: ['go-to-tab', 'diagnosis-setup-updated', 'diagnosis-started'],
  data() {
    return {
      selectedFilter: 'all',
      expandedSections: {},
      expandedAffectedQueries: {},
      listScrollY: 0,
      highlightFindingId: null,
      highlightTimer: null,
      startingDiagnosis: false,
      savingDismissal: false,
      diagnosisSetupError: '',
      awaitingFirstReports: false
    }
  },
  computed: {
    ...mapGetters(['lacksIntelligencePlan', 'isFreeTierPlan']),
    allFindings() {
      if (Array.isArray(this.diagnosisFindings) && this.diagnosisFindings.length > 0) {
        return withFindingKeys(this.diagnosisFindings)
      }

      // Backward-compatible fallback: derive findings from per-query diagnosis_report payloads.
      return (this.queries || []).flatMap((query) => {
        const report = query?.diagnosis_report
        if (!report || !this.isDoneStatus(report.status)) return []
        const findings = normalizeFindings(report.findings)
        return findings.map((finding, index) => ({
          ...finding,
          key: `${query.id}-${finding.code || index}`,
          query_id: query.id,
          query_text: query.query_text
        }))
      })
    },
    prioritizedFindings() {
      return sortFindingsByPriority(this.allFindings)
    },
    filteredFindings() {
      return this.prioritizedFindings.filter((item) => this.matchesSelectedFilter(item))
    },
    paywallPreviewIds() {
      return paywallPreview(this.diagnosisPreview, this.prioritizedFindings)
    },
    // Paywalled plans get one flat list: the readable findings, then the blurred teasers.
    filteredFindingsVisible() {
      if (!this.lacksIntelligencePlan) return this.filteredFindings
      return paywallPreviewOrder(this.diagnosisPreview, this.prioritizedFindings)
        .filter((item) => this.matchesSelectedFilter(item))
    },
    flatListHeading() {
      if (this.selectedFilter === 'all') return 'YOUR TOP ACTIONS'
      return `${this.filterLabel.toUpperCase()} — FILTERED ACTIONS`
    },
    filterLabel() {
      const labels = {
        critical: 'Critical findings',
        high: 'High priority',
        medium: 'Medium priority',
        quick_wins: 'Quick wins'
      }
      return labels[this.selectedFilter] || 'All'
    },
    // Quick wins cuts across severities, so it stays a flat list. So does the paywalled preview.
    isGroupedView() {
      if (this.lacksIntelligencePlan) return false
      return this.selectedFilter === 'all' || SEVERITY_FILTERS.includes(this.selectedFilter)
    },
    /**
     * Severity groups, each split into effort buckets. Counts are the full totals (what a
     * paywalled user could unlock); items are only what the paywall lets us render.
     * Low has no filter card, so it only shows on the unfiltered view.
     */
    groupedFindings() {
      const severities = this.selectedFilter === 'all' ? SEVERITY_GROUPS : [this.selectedFilter]
      return severities.map((severity) => {
        const inGroup = this.filteredFindings.filter((item) => this.severityGroupKey(item) === severity)
        const buckets = EFFORT_BUCKETS.map((bucket) => {
          const all = inGroup.filter((item) => this.bucketKey(item) === bucket.key)
          return {
            ...bucket,
            count: all.length,
            items: all.filter((item) => this.findingIsWithinPaywallDisplay(item))
          }
        })
        return { severity, label: SEVERITY_LABELS[severity], count: inGroup.length, buckets }
      })
    },
    highPriorityCount() {
      return this.allFindings.filter((f) => (f.severity || '').toLowerCase() === 'high').length
    },
    criticalCount() {
      return this.allFindings.filter((f) => (f.severity || '').toLowerCase() === 'critical').length
    },
    mediumPriorityCount() {
      return this.allFindings.filter((f) => (f.severity || '').toLowerCase() === 'medium').length
    },
    lowEffortCount() {
      return this.allFindings.filter((f) => (f.fix_effort || '').toLowerCase() === 'low').length
    },
    diagnosisInProgress() {
      return (this.queries || []).some((query) => {
        const report = query?.diagnosis_report
        if (!report) return false
        return this.isPendingStatus(report.status)
      })
    },
    hasDoneDiagnosisReports() {
      return (this.queries || []).some((query) => this.isDoneStatus(query?.diagnosis_report?.status))
    },
    diagnosisProgressMessage() {
      if (this.hasDoneDiagnosisReports) {
        return 'Some recommendations are ready. More recommendations will be available soon...'
      }
      return "Diagnosing your brand's recommended actions..."
    },
    diagnosisProviderCount() {
      return this.diagnosisSetup?.provider_count || 0
    },
    matchStringCount() {
      const aliases = this.reviewEntity?.brand_match_aliases
      return Array.isArray(aliases) ? aliases.length : 0
    },
    diagnosisDismissals() {
      return this.diagnosisSetup?.dismissals || { providers: false, match_strings: false }
    },
    hasAnyDiagnosisReport() {
      return (this.queries || []).some((query) => Boolean(query?.diagnosis_report))
    },
    // awaitingFirstReports bridges the click to the refetch that brings the pending
    // reports. It is local on purpose: keying it off diagnosis_setup.started would
    // spin forever on a historical date or after every report failed.
    showDiagnosisProgress() {
      return this.diagnosisInProgress || this.awaitingFirstReports
    },
    // A finished scan with no diagnosis started yet (diagnosis_setup.assessment_id is
    // only set once a scan has completed).
    canOfferDiagnosisRun() {
      const setup = this.diagnosisSetup
      return Boolean(setup?.assessment_id) && !setup.started && this.allFindings.length === 0
    },
    showSetupChecklist() {
      return this.canOfferDiagnosisRun && !this.isFreeTierPlan
    },
    showDiagnosisUpgradeCard() {
      return this.canOfferDiagnosisRun && this.isFreeTierPlan
    },
    showDiagnosisRunningCard() {
      return this.showDiagnosisProgress && this.allFindings.length === 0
    },
    // After a run has settled with nothing to show, say why instead of "not available yet".
    diagnosisUnavailableMessage() {
      const reports = (this.queries || []).map((query) => query?.diagnosis_report).filter(Boolean)
      const settled = reports.length > 0 && !reports.some((report) => this.isPendingStatus(report.status))
      if (this.diagnosisSetup?.started && settled) {
        if (reports.some((report) => this.isDoneStatus(report.status))) {
          return 'Your diagnosis finished and found no recommended actions for this scan.'
        }
        return "We couldn't finish your diagnosis for this scan. Contact support and we'll run it again."
      }
      return 'Diagnosis reports are not available yet for this assessment.'
    },
    showDiagnosisUnavailableAlert() {
      return this.allFindings.length === 0 &&
        !this.showSetupChecklist &&
        !this.showDiagnosisUpgradeCard &&
        !this.showDiagnosisRunningCard
    },
    totalQueryCount() {
      const fromQueries = Array.isArray(this.queries) ? this.queries.length : 0
      if (fromQueries > 0) return fromQueries
      const ids = new Set(this.allFindings.map((f) => f.query_id).filter(Boolean))
      return ids.size
    },
    unlockBannerPreviewShown() {
      return this.paywallPreviewIds.readableIds.length
    },
    unlockBannerTotalActions() {
      return this.prioritizedFindings.length
    },
    /** Banner sits immediately above this card (scroll reading order: severity groups → effort buckets, or filtered list). */
    firstBlurredItemKey() {
      if (!this.lacksIntelligencePlan) return null
      const ordered = this.isGroupedView
        ? this.groupedFindings.flatMap((group) => group.buckets.flatMap((bucket) => bucket.items))
        : [...this.filteredFindingsVisible]
      const hit = ordered.find((item) => this.shouldBlurItem(item))
      return hit ? hit.key : null
    },
    remainingHiddenActionsCount() {
      if (!this.lacksIntelligencePlan) return 0
      const { readableIds, teaserIds } = this.paywallPreviewIds
      return Math.max(0, this.prioritizedFindings.length - readableIds.length - teaserIds.length)
    },
    showPaywallMoreActionsFooter() {
      return this.lacksIntelligencePlan && this.remainingHiddenActionsCount > 0
    },
    selectedFindingId() {
      const id = this.$route.query.finding_id
      const value = Array.isArray(id) ? id[0] : id
      return typeof value === 'string' && value ? value : null
    },
    showingFindingDetail() {
      return Boolean(this.selectedFindingId)
    },
    showingSolutionDetail() {
      const step = this.$route.query.step
      const value = Array.isArray(step) ? step[0] : step
      return this.showingFindingDetail && value === 'solution'
    },
    selectedFindingReportId() {
      const id = String(this.selectedFindingId || '')
      const finding = this.prioritizedFindings.find(
        (item) => String(item.id) === id || String(item.key) === id
      )
      return finding ? finding.diagnosis_report_id : null
    }
  },
  watch: {
    hasAnyDiagnosisReport(hasReport) {
      if (hasReport) this.awaitingFirstReports = false
    },
    reviewEntityId() {
      this.awaitingFirstReports = false
      this.diagnosisSetupError = ''
    },
    selectedFindingId(newId, oldId) {
      if (newId && !oldId) {
        this.listScrollY = this.readScrollY()
        this.$nextTick(() => this.writeScrollY(0))
        return
      }
      if (!newId && oldId) {
        this.flashReturnedFinding(oldId)
        this.$nextTick(() => {
          requestAnimationFrame(() => this.restoreListScroll(oldId))
        })
      }
    }
  },
  beforeUnmount() {
    if (this.highlightTimer) clearTimeout(this.highlightTimer)
  },
  methods: {
    toggleFilter(filterKey) {
      this.selectedFilter = this.selectedFilter === filterKey ? 'all' : filterKey
    },
    isDoneStatus(status) {
      const value = String(status || '').trim().toLowerCase()
      return value === 'done'
    },
    isPendingStatus(status) {
      const value = String(status || '').trim().toLowerCase()
      return ['pending', 'processing', 'dispatching'].includes(value)
    },
    async setDiagnosisDismissal({ item, dismissed }) {
      if (!this.reviewEntityId || this.savingDismissal) return
      this.savingDismissal = true
      this.diagnosisSetupError = ''
      try {
        const response = await axios.patch(
          `${BASE_URL}/review_entities/${this.reviewEntityId}/diagnosis_checklist_dismissal`,
          { item, dismissed },
          { headers: { Authorization: localStorage.getItem('auth_token') } }
        )
        this.$emit('diagnosis-setup-updated', response.data.diagnosis_setup)
      } catch (error) {
        this.diagnosisSetupError = error.response?.data?.error || 'Could not save that change. Try again.'
      } finally {
        this.savingDismissal = false
      }
    },
    async runDiagnosis() {
      if (!this.reviewEntityId || this.startingDiagnosis) return
      this.startingDiagnosis = true
      this.diagnosisSetupError = ''
      try {
        const response = await axios.post(
          `${BASE_URL}/review_entities/${this.reviewEntityId}/run_diagnosis`,
          {},
          { headers: { Authorization: localStorage.getItem('auth_token') } }
        )
        this.awaitingFirstReports = true
        this.$emit('diagnosis-started', response.data.diagnosis_setup)
      } catch (error) {
        // 409: another tab or a teammate already started it, so show the running state.
        if (error.response?.status === 409) {
          this.awaitingFirstReports = true
          this.$emit('diagnosis-started', error.response.data.diagnosis_setup)
        } else {
          this.diagnosisSetupError = error.response?.data?.error || 'Could not start the diagnosis. Try again.'
        }
      } finally {
        this.startingDiagnosis = false
      }
    },
    affectedQueries(item) {
      return affectedQueriesFor(item, this.allFindings)
    },
    isAffectedExpanded(item) {
      return !!this.expandedAffectedQueries[item.key]
    },
    toggleAffectedQueries(item) {
      this.expandedAffectedQueries = {
        ...this.expandedAffectedQueries,
        [item.key]: !this.expandedAffectedQueries[item.key]
      }
    },
    matchesSelectedFilter(item) {
      if (this.selectedFilter === 'all') return true
      if (this.selectedFilter === 'quick_wins') return (item.fix_effort || '').toLowerCase() === 'low'
      return (item.severity || '').toLowerCase() === this.selectedFilter
    },
    findingIsWithinPaywallDisplay(item) {
      return findingIsWithinPaywallDisplay(
        this.lacksIntelligencePlan, this.diagnosisPreview, this.prioritizedFindings, item
      )
    },
    shouldBlurItem(item) {
      return shouldBlurFinding(this.lacksIntelligencePlan, this.diagnosisPreview, this.prioritizedFindings, item)
    },
    openFinding(item) {
      if (this.shouldBlurItem(item) || !item?.id) return
      const query = { ...this.$route.query }
      const entityId = this.reviewEntityId || query.review_entity_id
      if (entityId) query.review_entity_id = entityId
      if (this.selectedProvider) query.llm_provider = this.selectedProvider
      query.finding_id = String(item.id)
      this.$router.push({ path: '/analytics', query })
    },
    isHighlightedFinding(item) {
      return this.highlightFindingId && String(item.id) === String(this.highlightFindingId)
    },
    flashReturnedFinding(id) {
      this.highlightFindingId = String(id)
      if (this.highlightTimer) clearTimeout(this.highlightTimer)
      this.highlightTimer = setTimeout(() => {
        this.highlightFindingId = null
      }, 2400)
    },
    scrollRoot() {
      const main = document.querySelector('.v-main')
      if (main && main.scrollHeight > main.clientHeight + 20) return main
      return window
    },
    readScrollY() {
      const root = this.scrollRoot()
      return root === window ? window.scrollY : root.scrollTop
    },
    writeScrollY(y) {
      const root = this.scrollRoot()
      if (root === window) window.scrollTo(0, y)
      else root.scrollTop = y
    },
    restoreListScroll(findingId) {
      this.writeScrollY(this.listScrollY)
      const el = this.$el.querySelector(`[data-finding-id="${CSS.escape(String(findingId))}"]`)
      if (!el) return
      const rect = el.getBoundingClientRect()
      const inView = rect.top >= 96 && rect.bottom <= window.innerHeight - 24
      if (!inView) el.scrollIntoView({ block: 'center' })
    },
    // Unknown severities go to Low so a finding is never silently dropped.
    severityGroupKey(item) {
      const severity = (item.severity || '').toLowerCase()
      return SEVERITY_GROUPS.includes(severity) ? severity : 'low'
    },
    // Unknown effort goes to Evaluate so a finding is never silently dropped.
    bucketKey(item) {
      const effort = (item.fix_effort || '').toLowerCase()
      if (effort === 'low') return 'do_first'
      if (effort === 'medium') return 'plan_next'
      return 'evaluate'
    },
    bucketHeading(group, bucket) {
      return `${group.label} — ${bucket.label} (${bucket.effortLabel}) (${bucket.count})`.toUpperCase()
    },
    isSectionExpanded(key) {
      return Boolean(this.expandedSections[key])
    },
    toggleSection(key) {
      this.expandedSections = { ...this.expandedSections, [key]: !this.expandedSections[key] }
    }
  }
}
</script>

<style scoped>
.action-center-header-lightning {
  color: var(--brand-ocean-600) !important;
}

.action-center-card {
  border-radius: 16px;
  border-color: var(--brand-sand-300) !important;
  box-shadow: 0 2px 8px rgba(13, 30, 53, 0.06);
}

.summary-row {
  margin-top: 0;
  margin-bottom: 0;
}

.summary-metric-card {
  border: 1px solid var(--brand-sand-300);
  border-radius: 12px;
  padding: 12px 14px;
  background: #fff;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  position: relative;
}

.summary-label {
  font-size: 12px;
  color: var(--brand-ink-800);
  margin-bottom: 4px;
}

.summary-value {
  font-size: 28px;
  line-height: 1.1;
  font-weight: 700;
  color: var(--brand-ink-950);
}

.summary-metric-card:hover {
  transform: translateY(-1px);
}

.summary-metric-active {
  box-shadow:
    0 0 0 3px rgba(33, 105, 168, 0.25),
    0 8px 18px rgba(13, 30, 53, 0.14);
  border-color: var(--brand-ocean-600) !important;
  transform: translateY(-1px);
}

.summary-metric-active .summary-label {
  color: var(--brand-ocean-600);
  font-weight: 700;
}

.summary-metric-active .summary-value {
  color: var(--brand-ocean-600);
}

.summary-metric-critical {
  border-color: rgba(159, 45, 45, 0.28);
  background: linear-gradient(180deg, var(--brand-weak-100) 0%, #ffffff 100%);
}

.summary-metric-high {
  border-color: rgba(233, 168, 74, 0.38);
  background: linear-gradient(180deg, var(--brand-amber-100) 0%, #ffffff 100%);
}

.summary-metric-medium {
  border-color: rgba(77, 157, 223, 0.3);
  background: linear-gradient(180deg, var(--brand-ocean-100) 0%, #ffffff 100%);
}

.summary-metric-quick {
  border-color: rgba(26, 172, 176, 0.3);
  background: linear-gradient(180deg, var(--brand-teal-100) 0%, #ffffff 100%);
}

.summary-metric-selected-indicator {
  position: absolute;
  top: 8px;
  right: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #ffffff;
  background: var(--brand-ocean-600);
  border-radius: 999px;
  padding: 2px 8px;
}

.action-center-content {
  width: 100%;
}

.action-sections {
  margin-top: 8px;
}

.section-heading {
  font-size: 11px;
  font-weight: 700;
  color: var(--brand-ink-800);
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}

.action-bucket + .action-bucket {
  margin-top: 16px;
}

.section-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.section-heading-empty {
  opacity: 0.55;
}

.clear-filter-btn {
  margin-left: 4px;
  color: var(--brand-ocean-600);
  text-decoration: underline;
}

.action-center-paywall-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-top: 28px;
  padding-bottom: 8px;
}

.action-center-paywall-footer-hint {
  margin: 0 0 16px;
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(13, 36, 64, 0.62);
  max-width: 520px;
}
</style>
